import { useCallback, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { Lock, Mail, Sparkles } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'

import useApiService from 'services/api'
import { cn } from 'components/UI/cn'
import { resolveAgentIcon } from 'components/RecommendationsCard/agentIcons'
import type { RecommendationAgent } from 'models/companies'
import type { EmailIntegration } from 'models/emailIntegration'
import { useAuth } from 'context/authentication/useAuth'
import { Tooltip } from 'components/Tooltip/tooltip'

// Agents whose sub-features depend on full-inbox access. Kept in sync with
// the backend AGENT_REGISTRY (server/recommendations/pipeline/registry.py):
// - `contact_suggestion` owns the `contact_suggestion` signal type
//   (its create-flavor needs full-inbox to see cold-inbound senders).
// - `update_deal` owns both `email_stage_change` and `create_deal`; the
//   `create_deal` half requires full-inbox ingestion.
const FULL_INBOX_DEPENDENT_AGENT_IDS = new Set([
  'contact_suggestion',
  'update_deal',
])
const EMAIL_INTEGRATIONS_QUERY_KEY = ['ai-settings', 'email-integrations']

const FILTER_LEVELS = [
  {
    value: 'turn_off',
    label: 'Turn Off',
    description: 'Disables the daily AI summary for all users',
  },
  {
    value: 'conservative',
    label: 'Conservative',
    description: 'Only high-confidence, clearly actionable items',
  },
  {
    value: 'normal',
    label: 'Normal',
    description: 'Balanced filtering (recommended)',
  },
  {
    value: 'aggressive',
    label: 'Aggressive',
    description: 'Cast a wide net, surfacing more signals with lower certainty',
  },
] as const

const LLM_CONTEXT_MAX_LENGTH = 2000
const LLM_CONTEXT_PLACEHOLDER = `Example: We sell plant-based proteins to K-12 districts and universities. Minimum order is 500 cases. Our primary distributors are Sysco and US Foods. Sales cycle is typically 3-6 months for new accounts.`

/** Pill toggle shared by the agent rows (left sidebar) and the
 *  per-integration permissions rows. */
function ToggleSwitch({
  enabled,
  saving,
  onClick,
  ariaLabel,
}: {
  enabled: boolean
  saving: boolean
  onClick: () => void
  ariaLabel?: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={ariaLabel}
      disabled={saving}
      onClick={onClick}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors',
        enabled ? 'bg-brand' : 'bg-surface-disabled',
        saving && 'opacity-50 cursor-not-allowed'
      )}
    >
      <span
        className={cn(
          'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-surface shadow-sm transition-transform mt-0.5',
          enabled ? 'translate-x-4 ml-0.5' : 'translate-x-0 ml-0.5'
        )}
      />
    </button>
  )
}

export function AISettingsPage() {
  const api = useApiService()
  const { user } = useAuth()
  const { data: companyData, isLoading, refetch } = api.useGetCompanyDetails()

  const {
    data: emailIntegrationsData,
    isLoading: isLoadingIntegrations,
    refetch: refetchIntegrations,
  } = useQuery({
    queryKey: EMAIL_INTEGRATIONS_QUERY_KEY,
    queryFn: ({ signal }) =>
      api.emailIntegration.getEmailIntegrationList({ limit: 100 }, signal),
  })

  if (isLoading || !companyData || isLoadingIntegrations) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    )
  }

  // Agents are defined by the backend AGENT_REGISTRY and exposed on the
  // company-details payload. If the server is older and doesn't include
  // them, fall back to an empty list — settings still renders, the agent
  // section just stays empty until the deploy completes.
  const agents = companyData.recommendation_agents ?? []
  // Only the requesting user's own integrations are toggleable here —
  // the list endpoint returns shared teammate integrations too, but the
  // backend PATCH guard 403s anyone but the owner, so hide them from the
  // UI rather than render rows the user cannot interact with.
  const myIntegrations: EmailIntegration[] = (
    emailIntegrationsData?.results ?? []
  ).filter((row) => row.user === user?.id)

  return (
    <AISettingsForm
      key={companyData.id}
      initialFilterLevel={companyData.signal_filter_level ?? 'normal'}
      initialLlmContext={companyData.llm_context ?? ''}
      agents={agents}
      initialEnabledSignalTypes={
        companyData.enabled_signal_types ??
        agents.flatMap((a) => a.signal_types)
      }
      emailIntegrations={myIntegrations}
      onSaveFilterLevel={async (filterLevel) => {
        await api.updateCompanyDetails({ signalFilterLevel: filterLevel })
        await refetch()
      }}
      onSaveLlmContext={async (llmContext) => {
        await api.updateCompanyDetails({ llmContext })
        await refetch()
      }}
      onSaveEnabledSignalTypes={async (types) => {
        await api.updateCompanyDetails({ enabledSignalTypes: types })
        await refetch()
      }}
      onSaveInboxAccess={async (integrationId, inboxAccess) => {
        await api.emailIntegration.updateEmailIntegration(integrationId, {
          inbox_access: inboxAccess,
        })
        await refetchIntegrations()
      }}
    />
  )
}

function AISettingsForm({
  initialFilterLevel,
  initialLlmContext,
  agents,
  initialEnabledSignalTypes,
  emailIntegrations,
  onSaveFilterLevel,
  onSaveLlmContext,
  onSaveEnabledSignalTypes,
  onSaveInboxAccess,
}: {
  initialFilterLevel: string
  initialLlmContext: string
  agents: RecommendationAgent[]
  initialEnabledSignalTypes: string[]
  emailIntegrations: EmailIntegration[]
  onSaveFilterLevel: (filterLevel: string) => Promise<void>
  onSaveLlmContext: (llmContext: string) => Promise<void>
  onSaveEnabledSignalTypes: (types: string[]) => Promise<void>
  onSaveInboxAccess: (
    integrationId: number,
    inboxAccess: EmailIntegration['inbox_access']
  ) => Promise<void>
}) {
  const [filterLevel, setFilterLevel] = useState(initialFilterLevel)
  const [llmContext, setLlmContext] = useState(initialLlmContext)
  const [enabledTypes, setEnabledTypes] = useState<string[]>(
    initialEnabledSignalTypes
  )
  const [savingField, setSavingField] = useState<string | null>(null)
  const savedContextRef = useRef(initialLlmContext)

  const hasFullInboxIntegration = useMemo(
    () =>
      emailIntegrations.some(
        (integ) => integ.inbox_access === 'full_inbox_access'
      ),
    [emailIntegrations]
  )

  const handleToggleInboxAccess = useCallback(
    async (integration: EmailIntegration) => {
      const next: EmailIntegration['inbox_access'] =
        integration.inbox_access === 'full_inbox_access'
          ? 'contact_scoped_access'
          : 'full_inbox_access'
      setSavingField(`inbox_access_${integration.id}`)
      try {
        await onSaveInboxAccess(integration.id, next)
      } catch {
        toast.error('Failed to update full inbox access')
      } finally {
        setSavingField(null)
      }
    },
    [onSaveInboxAccess]
  )

  // An agent is "enabled" when at least one of its member signal types is
  // enabled. Toggling writes the agent's full signal_types set in or out
  // — never a subset — so partial states cannot be created from the UI.
  const isAgentEnabled = useCallback(
    (agent: RecommendationAgent) =>
      agent.signal_types.some((st) => enabledTypes.includes(st)),
    [enabledTypes]
  )

  const handleToggleAgent = useCallback(
    async (agent: RecommendationAgent) => {
      const currentlyEnabled = agent.signal_types.some((st) =>
        enabledTypes.includes(st)
      )
      const newTypes = currentlyEnabled
        ? enabledTypes.filter((t) => !agent.signal_types.includes(t))
        : Array.from(new Set([...enabledTypes, ...agent.signal_types]))
      setEnabledTypes(newTypes)
      setSavingField(`toggle_${agent.id}`)
      try {
        await onSaveEnabledSignalTypes(newTypes)
      } catch {
        toast.error('Failed to update agent setting')
        setEnabledTypes(initialEnabledSignalTypes)
      } finally {
        setSavingField(null)
      }
    },
    [enabledTypes, onSaveEnabledSignalTypes, initialEnabledSignalTypes]
  )

  const agentRows = useMemo(
    () =>
      agents.map((agent) => ({
        agent,
        Icon: resolveAgentIcon(agent.icon),
        enabled: isAgentEnabled(agent),
        saving: savingField === `toggle_${agent.id}`,
      })),
    [agents, isAgentEnabled, savingField]
  )

  const handleFilterLevelChange = useCallback(
    async (value: string) => {
      setFilterLevel(value)
      setSavingField('filter_level')
      try {
        await onSaveFilterLevel(value)
      } catch {
        toast.error('Failed to update filter level')
        setFilterLevel(initialFilterLevel)
      } finally {
        setSavingField(null)
      }
    },
    [onSaveFilterLevel, initialFilterLevel]
  )

  const handleContextBlur = useCallback(async () => {
    if (llmContext === savedContextRef.current) return
    setSavingField('llm_context')
    try {
      await onSaveLlmContext(llmContext)
      savedContextRef.current = llmContext
    } catch {
      toast.error('Failed to save business context')
    } finally {
      setSavingField(null)
    }
  }, [llmContext, onSaveLlmContext])

  return (
    <div className="w-full h-full flex flex-1 min-h-0">
      {/* Left sidebar — gray background like deals detail page */}
      <div className="w-[27rem] shrink-0 border-r border-line bg-surface-alt flex flex-col overflow-y-auto">
        <div className="px-5 py-4">
          <h1 className="text-xl font-bold text-fg-dark inline-flex items-center gap-1.5">
            <Sparkles size={18} />
            AI Agents
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Pre-trained on foodservice sales best practices and channels.
          </p>
        </div>
        <div className="px-5 pb-5 space-y-3">
          {agentRows.map(({ agent, Icon, enabled, saving }) => {
            return (
              <div
                key={agent.id}
                className={cn(
                  'rounded-lg border bg-surface p-4 transition-opacity',
                  enabled ? 'border-line' : 'border-line opacity-60'
                )}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <Icon size={18} className="text-brand" />
                    <span className="text-sm font-medium text-fg">
                      {agent.label}
                    </span>
                    {FULL_INBOX_DEPENDENT_AGENT_IDS.has(agent.id) &&
                      !hasFullInboxIntegration && (
                        <Tooltip
                          compact
                          placement="top"
                          content="Some actions (create contact / create deal) require Full Inbox Access on at least one of your email integrations."
                        >
                          <span
                            aria-label="Some actions require Full Inbox Access"
                            className="inline-flex"
                          >
                            <Lock size={12} className="text-fg-tertiary" />
                          </span>
                        </Tooltip>
                      )}
                  </div>
                  <ToggleSwitch
                    enabled={enabled}
                    saving={saving}
                    onClick={() => handleToggleAgent(agent)}
                    ariaLabel={`Toggle ${agent.label}`}
                  />
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">
                  {agent.description}
                </p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Settings — right side, white background fills full width */}
      <div className="flex-1 overflow-y-auto bg-surface">
        <div className="px-[42px] pb-6 pt-[4.5rem] max-w-2xl space-y-8">
          {/* Company Context */}
          <section>
            <h3 className="text-base font-semibold text-fg mb-1">
              About Your Business
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Help our AI Agents understand your business so recommendations are
              more targeted and relevant. Describe your products, target market,
              distributors, and typical sales cycle.
            </p>
            <textarea
              value={llmContext}
              onChange={(e) => {
                if (e.target.value.length <= LLM_CONTEXT_MAX_LENGTH) {
                  setLlmContext(e.target.value)
                }
              }}
              onBlur={handleContextBlur}
              placeholder={LLM_CONTEXT_PLACEHOLDER}
              rows={5}
              className="w-full rounded-lg border border-line bg-surface p-3 text-sm text-fg placeholder:text-fg-tertiary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand resize-y"
            />
            <div className="flex justify-between mt-1">
              <p className="text-xs text-fg-tertiary">
                {savingField === 'llm_context' ? 'Saving...' : '\u00A0'}
              </p>
              <p className="text-xs text-fg-tertiary">
                {llmContext.length}/{LLM_CONTEXT_MAX_LENGTH}
              </p>
            </div>
          </section>

          {/* Signal Filter Level */}
          <section>
            <h3 className="text-base font-semibold text-fg mb-1">
              Recommendation Sensitivity
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Controls how many recommendations surface each day. Conservative
              shows only the highest-confidence signals; Aggressive casts a
              wider net.
            </p>
            <div className="space-y-2">
              {FILTER_LEVELS.map((level) => (
                <label
                  key={level.value}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    filterLevel === level.value
                      ? 'border-brand bg-surface-hover'
                      : 'border-line hover:bg-surface-hover',
                    savingField === 'filter_level' &&
                      'opacity-50 pointer-events-none'
                  )}
                >
                  <input
                    type="radio"
                    name="filter_level"
                    value={level.value}
                    checked={filterLevel === level.value}
                    onChange={() => handleFilterLevelChange(level.value)}
                    className="mt-0.5 accent-brand"
                  />
                  <div>
                    <span className="text-sm font-medium text-fg">
                      {level.label}
                    </span>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {level.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </section>

          {/* Permissions — per-integration full-inbox access */}
          <section className="border-t border-line pt-8">
            <h3 className="text-base font-semibold text-fg mb-1">
              Permissions
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Control what data AI Agents can access. Some agents require
              additional permissions to function.
            </p>
            {emailIntegrations.length === 0 ? (
              <p className="text-sm text-fg-tertiary italic">
                Connect an email to manage permissions.
              </p>
            ) : (
              <div className="space-y-2">
                {emailIntegrations.map((integration) => {
                  const enabled =
                    integration.inbox_access === 'full_inbox_access'
                  const saving =
                    savingField === `inbox_access_${integration.id}`
                  return (
                    <div
                      key={integration.id}
                      className="rounded-lg border border-line bg-surface p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Mail size={16} className="text-brand shrink-0" />
                            <span className="text-sm font-medium text-fg truncate">
                              Full inbox access
                            </span>
                          </div>
                          <p className="text-xs text-gray-500 leading-relaxed mt-1">
                            Allows AI Agents to read emails from unknown senders
                            to surface new contacts and deals. Required for
                            Contact Updates and Deal Updates create flows.
                          </p>
                          {emailIntegrations.length > 1 && (
                            <p className="text-xs text-fg-tertiary mt-1">
                              {integration.email}
                            </p>
                          )}
                        </div>
                        <ToggleSwitch
                          enabled={enabled}
                          saving={saving}
                          onClick={() => handleToggleInboxAccess(integration)}
                          ariaLabel={`Toggle full inbox access for ${integration.email}`}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
